import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Button, Divider, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';

import { Task } from '../../lib/interfaces/Task';
import { User } from '../../lib/interfaces/User';
// import ContentRenderer from '../Smart/Content/ContentRenderer';
import Comments from '../Comments/Comments';
import { toggleCompleteTask } from '../../api/apis';
import useUserMeQuery from '../../reactQuery/hooks/useUserMeQuery';
import reactQueryKeys from '../../reactQuery/reactQueryKeys';
import CompleteButton from '../Simple/CompleteButton/CompleteButton';
import { Course } from '../../lib/interfaces/Course';
import { findFirstIncompletedTask } from '../../lib/helpers/courses.helper';
import { IEntityContentField } from '../../lib/interfaces/Common';
import { FIELD_TYPES } from '../Form/form.utils';
import QuizLayout from '../Smart/Quiz/QuizLayout';
// import CodeTaskLayout from '../Smart/CodeEditor/CodeTaskLayout';
import CodeTaskReference from '../Smart/CodeEditor/CodeTaskReference';
import Hint from '../Smart/Hint/Hint';
import PlanDemand from '../SubscriptionPlans/PlanDemand';
import Overview from '../Smart/ContentStaticBlocks/Overview/Overview';
import Conclusion from '../Smart/ContentStaticBlocks/Conclusion/Conclusion';
import VideoReference from '../Smart/VideoReference/VideoReference';
import { getAllCourseOccupations } from '../../api/apiRequests/courseOccupations';
import { day } from '../../lib/helpers/time.helper';
import { ICourseOccupation } from '../../lib/interfaces/CourseOccupation';
import './TaskContent.scss';
import ContentBlock from '../Smart/ContentStaticBlocks/ContentBlock/ContentBlock';
import CodeExample from '../Smart/CodeExample/CodeExample';
import FileRenderer from '../Smart/FileRenderer/FileRenderer';
import {
	CONTENT_FIELDS,
	getContentFieldValue,
} from '../../lib/helpers/content.helper';
import { DASHBOARD_BASE_URL } from '../../api/endpointBuilder';
import CodeSandboxEmbed from '../Smart/CodesandboxEmbed/CodesandboxEmbed';
import CodeBlock from '../Smart/CodeBlock/CodeBlock';
// import SandPackWrapper from '../Smart/CodeSandpack/CodeSandpack';
import CodeSandPackAdapter from '../Smart/CodeSandpack/CodeSandPackAdapter';
import CodeSandpackWithContent from '../Smart/CodeSandpack/CodeSandpackWithContent';
import { useAnalyticsEventTracker } from '../../hooks/useAnalyticsEventTracker';
import AutomatedOverview from '../Smart/ContentStaticBlocks/Overview/AutomatedOverview';
import ApiControl from '../Smart/ApiControl/ApiControl';
import SecretContent from '../Smart/SecretContent/SecretContent';

type TaskSingleProps = {
	task: Task;
	course: Course;
};

const TaskSingle = ({ task: taskData, course }: TaskSingleProps) => {
	const { i18n } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const currentLanguage: string = i18n.language;
	const { eventTracker } = useAnalyticsEventTracker();
	const { data: currentUser } = useUserMeQuery();
	const { data: courseOccupations } = useQuery<ICourseOccupation[], Error>(
		['course-occupations'],
		() => getAllCourseOccupations(),
		{
			staleTime: day,
		},
	);
	const [isQuizCompleted, setIsQuizCompleted] = useState<boolean>(false);

	const { subscriptionNotMatched } = taskData;
	const contentFields: IEntityContentField[] = !subscriptionNotMatched
		? taskData?.content[currentLanguage] || []
		: [];

	const containRequiredQuiz = contentFields.find(
		(field) => field.type === FIELD_TYPES.QUIZ_REF && field.options?.required,
	);

	const isTaskDone = currentUser?.completedTasks?.find(
		(item) => item.id === taskData.id,
	);
	const getTaskName = () => {
		const nameField = contentFields.find(
			(field) => field.name === `name_${currentLanguage}`,
		);
		return nameField ? nameField.content : '';
	};

	const mutationToggleCompleteTask = useMutation<User, Error, Task['id']>(
		(taskId): any => toggleCompleteTask(taskId),
		{
			onSuccess(data: User) {
				queryClient.setQueryData(reactQueryKeys.userMe, (prevUser: any) => ({
					...prevUser,
					completedTasks: [...data.completedTasks],
				}));
				if (currentUser) currentUser.completedTasks = [...data.completedTasks];
			},
			onError: (err: Error) => console.error(err),
		},
	);

	const handleCompleteTaskButton = () => {
		eventTracker({
			category: 'Task',
			action: 'Complete task',
			label: taskData.slug,
			value: isTaskDone ? 1 : 0,
		});
		mutationToggleCompleteTask.mutate(taskData.id, {
			onSuccess: async () => {
				// TODO: theoretically we can make cache mutation and recalculate progress of FE side
				// instaed of whole query invalidation
				if (courseOccupations) {
					courseOccupations.forEach((occupation) => {
						queryClient.invalidateQueries(
							`${reactQueryKeys.courses}-${occupation.slug}`,
						);
					});
				}

				if (!isTaskDone) {
					const nextTaskSlug = findFirstIncompletedTask(course, currentUser);
					navigate(`/courses/${course.slug}/${nextTaskSlug}`);
				}
			},
		});
	};

	const taskTitle = getContentFieldValue({
		entityContent: taskData.content,
		field: CONTENT_FIELDS.NAME,
		language: currentLanguage,
	});

	// const [playVideo, setPlayVideo] = useState<Boolean>(false);

	// TODO: add loader/spinner while task is loading

	const fieldRenderer = (field: IEntityContentField) => {
		if (field.type === FIELD_TYPES.TEXT) {
			return (
				<div key={field.name}>
					<Typography variant="h5" component="h5" key={field.name}>
						{field.content}
					</Typography>
					<Divider />
				</div>
			);
		}
		if (
			field.type === FIELD_TYPES.AUTOMATED_OVERVIEW &&
			!taskData.isAutomatedOverviewDisabled
		) {
			return field?.options?.items?.length ? (
				<AutomatedOverview field={field} key={field.name} />
			) : null;
		}
		if (field.type === FIELD_TYPES.OVERVIEW_BLOCK) {
			return <Overview field={field} key={field.name} />;
		}
		if (field.type === FIELD_TYPES.CONCLUSION_BLOCK) {
			return <Conclusion field={field} key={field.name} />;
		}
		// if (field.type === FIELD_TYPES.RICH_EDITOR) {
		// 	return <ContentRenderer html={field.content} key={field.name} />;
		// }
		if (field.type === FIELD_TYPES.RICH_EDITOR) {
			return <ContentBlock field={field} key={field.name} />;
		}
		if (field.type === FIELD_TYPES.QUIZ_REF) {
			return (
				<QuizLayout
					key={field.name}
					field={field}
					handleQuizCompleted={setIsQuizCompleted}
				/>
			);
		}
		if (field.type === FIELD_TYPES.HINT) {
			return <Hint field={field} key={field.name} />;
		}
		if (field.type === FIELD_TYPES.CODE_TASK_REF) {
			return (
				<CodeTaskReference
					key={field.name}
					field={field}
					taskSlug={taskData.slug}
					taskId={taskData.id}
				/>
			);
		}
		if (field.type === FIELD_TYPES.VIDEO_REF) {
			return <VideoReference key={field.name} field={field} />;
		}
		if (field.type === FIELD_TYPES.CODE_EDITOR) {
			return <CodeExample key={field.name} field={field} />;
		}
		if (field.type === FIELD_TYPES.FILE) {
			return <FileRenderer key={field.name} field={field} />;
		}
		if (field.type === FIELD_TYPES.CODESANDBOX_EMBED) {
			return <CodeSandboxEmbed key={field.name} field={field} />;
			// return <SandPackWrapper />;
		}
		if (field.type === FIELD_TYPES.CODE_BLOCK) {
			return <CodeBlock key={field.name} field={field} />;
		}
		if (field.type === FIELD_TYPES.CODE_SAND_PACK) {
			return <CodeSandPackAdapter key={field.name} field={field} />;
		}
		if (field.type === FIELD_TYPES.SAND_PACK_WITH_TEXT) {
			return <CodeSandpackWithContent key={field.name} field={field} />;
		}
		if (field.type === FIELD_TYPES.API_CONTROL) {
			return <ApiControl key={field.name} field={field} />;
		}
		if (field.type === FIELD_TYPES.SECRET_CONTENT) {
			return <SecretContent key={field.name} field={field} />;
		}
		return null;
	};

	const getField = (field: IEntityContentField) => {
		if (field.name === `${CONTENT_FIELDS.NAME}_${currentLanguage}`) {
			return null;
		}
		const renderer = fieldRenderer(field);
		// console.log(
		// 	[
		// 		FIELD_TYPES.CODE_SAND_PACK,
		// 		FIELD_TYPES.CODE_TASK_REF,
		// 		FIELD_TYPES.SAND_PACK_WITH_TEXT,
		// 	].includes(field.type),
		// );

		return renderer ? (
			<div
				key={field.name}
				style={{ paddingTop: '40px' }}
				className={
					![
						FIELD_TYPES.CODE_SAND_PACK,
						FIELD_TYPES.CODE_TASK_REF,
						FIELD_TYPES.SAND_PACK_WITH_TEXT,
						FIELD_TYPES.CODE_EDITOR,
					].includes(field.type)
						? 'content-column'
						: ''
				}
			>
				{renderer}
			</div>
		) : null;
	};

	// Only for admins show button "Edit task"
	const showEditButton = currentUser?.role?.includes('admin');
	const editButtonUrl = `${DASHBOARD_BASE_URL}/edit-task/${taskData.slug}`;

	return (
		<div className="TaskSingle">
			{showEditButton && (
				<div style={{ position: 'absolute', right: 20, top: 0, zIndex: 2 }}>
					<a href={editButtonUrl} target="_blank" rel="noreferrer">
						<Button endIcon={<KeyboardTabIcon />}>Edit task</Button>
					</a>
				</div>
			)}
			{taskData && (
				<div className="content-column">
					<Typography
						variant="h3"
						component="h3"
						sx={{
							fontWeight: '600',
							paddingBottom: '10px',
						}}
						className="task-title"
					>
						{taskTitle}
					</Typography>
				</div>
			)}
			{taskData && contentFields.map((field) => getField(field))}

			{!subscriptionNotMatched && (
				<div className="content-column">
					<CompleteButton
						checked={!!isTaskDone}
						handleChange={handleCompleteTaskButton}
						disabled={mutationToggleCompleteTask.isLoading}
						taskName={getTaskName()}
						containRequiredQuiz={!!containRequiredQuiz}
						isQuizCompleted={isQuizCompleted}
					/>
					<Comments taskId={taskData.id} />
				</div>
			)}
			{/* <div>
				<span>SB:</span>
				<StackBlitzEmbedd sbId="vitejs-vite-9nypmy" />
			</div> */}
			{subscriptionNotMatched && <PlanDemand />}
		</div>
	);
};

export default TaskSingle;
